import MapboxDraw from '@mapbox/mapbox-gl-draw';
import type { Position } from 'geojson';
import hasSelfIntersection from '~/components/page/Settings/Offices/components/WorkingArea/utils/hasSelfIntersection';
import wouldNextPointSelfIntersect from '~/components/page/Settings/Offices/components/WorkingArea/utils/wouldNextPointSelfIntersect';

type DrawPolygonState = {
  polygon: MapboxDraw.DrawPolygon;
  currentVertexPosition: number;
};

type DrawEventWithTarget = {
  lngLat: { lng: number; lat: number };
  featureTarget?: { properties?: { meta?: string } | null };
};

const DrawPolygonMode = MapboxDraw.modes.draw_polygon;

const getCommittedCoordinates = (state: DrawPolygonState): Array<Position> =>
  (state.polygon.coordinates[0] ?? []).slice(0, state.currentVertexPosition);

const isVertexEvent = (e: DrawEventWithTarget): boolean =>
  e.featureTarget?.properties?.meta === 'vertex';

const isEnterKey = (e: KeyboardEvent): boolean => e.key === 'Enter';

const committedPolygonHasSelfIntersection = (
  state: DrawPolygonState,
): boolean => {
  const committedCoordinates = getCommittedCoordinates(state);
  const [start] = committedCoordinates;

  if (!start || committedCoordinates.length < 4) {
    return false;
  }

  return hasSelfIntersection({
    type: 'Feature',
    properties: {},
    geometry: {
      type: 'Polygon',
      coordinates: [[...committedCoordinates, start]],
    },
  });
};

const wouldEventSelfIntersect = (
  state: DrawPolygonState,
  e: DrawEventWithTarget,
): boolean => {
  if (isVertexEvent(e)) {
    return committedPolygonHasSelfIntersection(state);
  }

  return wouldNextPointSelfIntersect({
    committedCoordinates: getCommittedCoordinates(state),
    nextPoint: [e.lngLat.lng, e.lngLat.lat],
  });
};

const drawSimplePolygon: MapboxDraw.DrawCustomMode<DrawPolygonState> = {
  ...DrawPolygonMode,
  onMouseMove(state, e) {
    DrawPolygonMode.onMouseMove?.call(this, state, e);

    if (wouldEventSelfIntersect(state, e)) {
      this.updateUIClasses({ mouse: 'not-allowed' });
      return;
    }

    // Always clear not-allowed when the next point is valid again.
    // Parent already sets pointer when hovering a closable vertex.
    if (!isVertexEvent(e)) {
      this.updateUIClasses({ mouse: 'add' });
    }
  },
  onClick(state, e) {
    if (wouldEventSelfIntersect(state, e)) {
      this.updateUIClasses({ mouse: 'not-allowed' });
      return;
    }

    return DrawPolygonMode.onClick?.call(this, state, e);
  },
  onTap(state, e) {
    if (wouldEventSelfIntersect(state, e)) {
      this.updateUIClasses({ mouse: 'not-allowed' });
      return;
    }

    return DrawPolygonMode.onTap?.call(this, state, e);
  },
  onKeyUp(state, e) {
    if (isEnterKey(e) && committedPolygonHasSelfIntersection(state)) {
      this.updateUIClasses({ mouse: 'not-allowed' });
      return;
    }

    return DrawPolygonMode.onKeyUp?.call(this, state, e);
  },
};

export default drawSimplePolygon;
